import {
  Button,
  Card,
  Column,
  Columns,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { ESPECS_REPORT_TYPE_OPTIONS, IReportSection } from "./types";
import { DatePicker, DatePickerProvider } from "@origin-digital/ods-pickers";
import React, { useContext, useEffect, useState } from "react";
import { apiClient } from "../../api/apiService";
import { AppContext } from "../../context/AppContext";
import { addDays } from "date-fns";

const RequestReportSection = ({ setUnableToDownload }: IReportSection) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);
  const [requestReportType, setRequestReportType] =
    useState<string>("new-connection");
  const {
    context: { user },
  } = useContext(AppContext);

  useEffect(() => {
    if (startDate && endDate) {
      const latestValidEndDate = new Date(startDate);
      latestValidEndDate.setMonth(startDate.getMonth() + 3);

      setIsDateRangeValid(endDate > startDate && endDate < latestValidEndDate);
    } else {
      setIsDateRangeValid(true);
    }
  }, [startDate, endDate]);

  const downloadConnectionRequestsReport = async () => {
    try {
      const adjustedEndDate = addDays(endDate as Date, 1);
      const response = await apiClient.get(`/reports/${requestReportType}`, {
        params: {
          startDate: startDate?.toISOString(),
          endDate: adjustedEndDate?.toISOString(),
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "text/csv" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${requestReportType}.csv`);
      document.body.appendChild(link);
      link.click(); // Trigger the download
      link.remove();
      setUnableToDownload(false);
    } catch (e) {
      setUnableToDownload(true);
    }
  };

  return (
    <Card>
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        <Columns
          collapseBelow="md"
          space={{
            xs: "xsmall",
            md: "medium",
          }}
        >
          <Column width="2/3">
            <Stack space="large">
              <Stack>
                <Heading variant="h4" component="h1">
                  Generate extracts
                </Heading>
                <Text>Download a CSV file of connection requests.</Text>
              </Stack>
              <Stack space="xlarge">
                <RadioGroup
                  name="report-type"
                  label="Choose a report type"
                  value={requestReportType}
                  onChange={(e) => setRequestReportType(e.target.value)}
                >
                  {(user?.role === "ORIGIN_ADMIN" ||
                  user?.role === "ORIGIN_USER"
                    ? ESPECS_REPORT_TYPE_OPTIONS
                    : []
                  ).map(({ label, value }, i) => (
                    <Radio key={i} id={value} label={label} value={value} />
                  ))}
                </RadioGroup>
                <Columns collapseBelow="md" space="medium">
                  <Column>
                    <DatePickerProvider>
                      <DatePicker
                        id="datepicker"
                        label="Start date"
                        muiProps={{
                          slotProps: {
                            textField: { variant: "outlined" },
                          },
                        }}
                        value={startDate}
                        onChange={(value) => {
                          if (startDate != value) {
                            setStartDate(value);
                          }
                        }}
                        helperText={
                          isDateRangeValid
                            ? "You can choose a maximum range of 3 months"
                            : "Start date is more than 3 months from end date."
                        }
                        error={!isDateRangeValid}
                      />
                    </DatePickerProvider>
                  </Column>
                  <Column>
                    <DatePickerProvider>
                      <DatePicker
                        id="datepicker"
                        label="End date"
                        muiProps={{
                          slotProps: {
                            textField: { variant: "outlined" },
                          },
                        }}
                        value={endDate}
                        onChange={(value) => {
                          if (endDate != value) {
                            setEndDate(value);
                          }
                        }}
                        helperText={
                          isDateRangeValid
                            ? undefined
                            : "End date is more than 3 months from start date."
                        }
                        error={!isDateRangeValid}
                      />
                    </DatePickerProvider>
                  </Column>
                </Columns>
                <Button
                  aria-label="Download connection requests"
                  onClick={downloadConnectionRequestsReport}
                  disabled={
                    !startDate ||
                    !endDate ||
                    !isDateRangeValid ||
                    !requestReportType
                  }
                >
                  Download
                </Button>
              </Stack>
            </Stack>
          </Column>
          <Column width="1/3" />
        </Columns>
      </Stack>
    </Card>
  );
};

export default RequestReportSection;
